import React, { useEffect, useMemo, useRef, useState } from 'react';

import PropTypes from 'prop-types';
import cn from 'classnames';
import { loadReactSlick } from '../../../utils/lazy-heavy-libs';
import styles from './styles.module.scss';

const ArrowPrev = () => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      id="arrow"
      fillRule="evenodd"
      clipRule="evenodd"
      d="M16.9216 19.6001L23.4609 13.0608L21.3396 10.9395L12.679 19.6001L21.3396 28.2608L23.4609 26.1395L16.9216 19.6001Z"
      fill="white"
    />
  </svg>
);

const NextArrow = (props) => {
  // eslint-disable-next-line react/prop-types
  const { className, style, onClick } = props;
  return (
    <button
      type="button"
      className={cn(className, styles.nextArrow)}
      style={style}
      onClick={onClick}
    >
      <ArrowPrev />
    </button>
  );
};

const PrevArrow = (props) => {
  // eslint-disable-next-line react/prop-types
  const { className, style, onClick } = props;
  return (
    <button
      type="button"
      className={cn(className, styles.prevArrow)}
      style={style}
      onClick={onClick}
    >
      <ArrowPrev />
    </button>
  );
};

const settings = {
  className: styles.slider,
  dots: true,
  infinite: true,
  centerMode: true,
  centerPadding: '0px',
  slidesToShow: 3,
  slidesToScroll: 1,
  nextArrow: <NextArrow />,
  prevArrow: <PrevArrow />,
  customPaging: () => <div className={styles.dot} />,
  responsive: [
    {
      breakpoint: 1024,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 1,
      },
    },
    {
      breakpoint: 575,
      settings: {
        arrows: false,
        slidesToShow: 1,
        slidesToScroll: 1,
      },
    },
  ],
};
const QuotesSlider = ({ title = undefined, quotes = [] }) => {
  const [logos, setLogos] = useState({});
  const [showSlider, setShowSlider] = useState(false);
  const [SliderComponent, setSliderComponent] = useState(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const logosData = quotes?.reduce((acc, quote) => {
      if (quote.quoteLogo) {
        const img = document
          .getElementById(`quote-logo-${quote.quoteLogo.id}`)
          ?.getElementsByTagName('img')?.[0];
        const logo = img?.dataset?.src || img?.src;
        return { ...acc, [quote.quoteLogo.id]: logo };
      }
      return acc;
    }, {});
    setLogos(logosData);
  }, [quotes]);

  useEffect(() => {
    const timer = setTimeout(() => setShowSlider(true), 300);
    return () => clearTimeout(timer);
  }, []);

  const slides = useMemo(
    () =>
      quotes.map((quote, index) => {
        const logo = logos[quote.quoteLogo?.id];
        return (
          <div className={styles.slide} key={index}>
            <div className={styles.slideInner}>
              <div className={styles.slideInfo}>
                <h5>{quote.title}</h5>
                <p>{quote.description}</p>
              </div>
              {(quote.quoteName || quote.quotePosition) && (
                <div className={styles.quotePerson}>
                  {logo && (
                    <img
                      src={logo}
                      alt={quote.quoteName || ''}
                      loading="lazy"
                      decoding="async"
                      width={32}
                      height={32}
                    />
                  )}
                  <div>
                    {quote.quoteName && <strong>{quote.quoteName}</strong>}
                    {quote.quotePosition && <p>{quote.quotePosition}</p>}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      }),
    [quotes, logos]
  );

  useEffect(() => {
    let cancelled = false;

    const ensureLoaded = () =>
      loadReactSlick().then((Comp) => {
        if (cancelled) return;
        setSliderComponent(() => Comp);
      });

    if (SliderComponent) return undefined;
    if (typeof window === 'undefined') return undefined;

    const el = wrapperRef.current;
    if (!el || !('IntersectionObserver' in window)) {
      ensureLoaded();
      return () => {
        cancelled = true;
      };
    }

    const observer = new window.IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting || e.intersectionRatio > 0)) {
          observer.disconnect();
          ensureLoaded();
        }
      },
      { rootMargin: '600px 0px', threshold: 0.01 }
    );

    observer.observe(el);
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [SliderComponent]);

  return (
    <div className="container" ref={wrapperRef}>
      <div className={cn(styles.sliderBox, { [styles.visible]: showSlider })}>
        {title && <h4>{title}</h4>}
        <div>
          {SliderComponent ? (
            <SliderComponent {...settings}>{slides}</SliderComponent>
          ) : (
            <div className={styles.slider} aria-hidden="true">
              {slides.slice(0, 3)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

QuotesSlider.propTypes = {
  title: PropTypes.string,
  quotes: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string.isRequired,
      quoteName: PropTypes.string,
      quotePosition: PropTypes.string,
    })
  ).isRequired,
};

export default QuotesSlider;
